import React, { useMemo, useState } from 'react';

import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Can from '@shared/permissions/Can';
import useCan from '@shared/permissions/useCan';
import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

import { replenishmentService } from '@services/operator/replenishmentService';
import type { ReplenishmentSlot } from '@services/operator/replenishmentService';

// TableIndex 的 service prop 需要一個帶 getRecords 的物件，但 replenishmentService 上
// slot 列表方法叫 getShelfRecords（getRecords 已被補貨單列表佔用）。這裡做一層 adapter
// 把 getShelfRecords 對應到 TableIndex 約定的 getRecords。
const slotTableService = {
  getRecords: (payload: Record<string, unknown>) => replenishmentService.getShelfRecords(payload),
};

function ReplenishmentShelvesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canWrite = useCan()('inventories:write');
  // 新增 / 編輯改走獨立頁；導頁回來會 remount TableIndex 取最新資料。lastModified 仍保留
  // 給刪除後就地刷新使用（無需離開列表）。
  const [lastModified, setLastModified] = useState(0);

  // 刪除 slot：成功後刷新列表 (setLastModified)；錯誤碼 / 網路錯誤沿用
  // 同一套 handleApiError / handleNetworkError（後端 message → common.error.<code>）。
  const handleDelete = async (slot: ReplenishmentSlot) => {
    try {
      const response = await replenishmentService.destroyShelf(slot.id);
      if (response.success) {
        message.success(t('common.message.delete_success'));
        setLastModified(Date.now());
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t('common.error.delete_failed'),
        });
      }
    } catch (error) {
      handleNetworkError(error, {
        t,
        fallbackMessage: t('common.error.delete_failed'),
      });
    }
  };

  const columns: unknown[] = useMemo(() => [
    {
      name: t('operator.replenishment_shelves.columns.product'),
      selector: (row: ReplenishmentSlot) => row.product_name,
      // 商品欄連結到商品詳情頁（新分頁），對齊同模組 ReplenishmentOrders/Show.tsx 及
      // 設計規範的可點擊 ProductCell；名稱為連結藍字、SKU 為次要灰字。
      cell: (row: ReplenishmentSlot) => (
        <Link to={`/products/${row.product_id}`} target="_blank">
          <div>{row.product_name}</div>
          {row.product_sku && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.product_sku}
            </Typography.Text>
          )}
        </Link>
      ),
    },
    {
      name: t('operator.replenishment_shelves.columns.inventory_type'),
      selector: (row: ReplenishmentSlot) => row.inventory_type_name || '-',
    },
    {
      name: t('operator.replenishment_shelves.columns.shelf'),
      selector: (row: ReplenishmentSlot) => row.shelf_location || '-',
    },
    {
      name: t('operator.replenishment_shelves.columns.capacity'),
      selector: (row: ReplenishmentSlot) => row.capacity ?? '-',
    },
    {
      name: t('operator.replenishment_shelves.columns.replenishment_threshold'),
      selector: (row: ReplenishmentSlot) => row.replenishment_threshold ?? '-',
    },
    {
      name: t('common.actions'),
      cell: (row: ReplenishmentSlot) => (
        <Space size={0}>
          <Can requiredPermission="inventories:write">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/replenishment_shelves/${row.id}/edit`)}
            >
              {t('common.button.edit')}
            </Button>
          </Can>
          {/* delete 用 useCan() gate 而非 <Can> 包裹：Popconfirm 需把 onClick 注入它的直接
              child，但 <Can> 不會把 props 轉發給內層 Button，會導致 Popconfirm 永遠打不開。
              故改用 useCan() 算出 canWrite，Popconfirm 直接包住 Button，無權限時 disabled。 */}
          <Popconfirm
            title={t('operator.replenishment_shelves.delete_confirm')}
            okText={t('common.button.confirm')}
            cancelText={t('common.button.cancel')}
            onConfirm={() => handleDelete(row)}
            disabled={!canWrite}
          >
            <Button type="link" danger disabled={!canWrite} icon={<DeleteOutlined />}>
              {t('common.button.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], [t, canWrite, navigate]);

  return (
    <div>
      <ListPageHeader
        items={['replenishment', 'replenishment.shelves']}
        actions={
          <Can requiredPermission="inventories:write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/replenishment_shelves/new')}
            >
              {t('common.button.new')}
            </Button>
          </Can>
        }
      />
      <TableIndex
        columns={columns}
        tableId="ReplenishmentShelvesTable"
        service={slotTableService as unknown as React.ComponentProps<typeof TableIndex>['service']}
        lastModified={lastModified}
        setLastModified={setLastModified}
      />
    </div>
  );
}

export default ReplenishmentShelvesList;
